import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoginPayload } from '../types/commonTypes';
import AsyncStorage, { isWeb } from '../storage';
import SecureLS from 'secure-ls';

let storedUser: any = null;

// Initialize SecureLS for web
let secureLS: SecureLS | null = null;
if (typeof window !== 'undefined' && window.localStorage) {
  secureLS = new SecureLS({ encodingType: 'aes' });
}

// Function to initialize storedUser asynchronously
const initializeStoredUser = async () => {
  if (secureLS) {
    const user = secureLS.get('user');
    return user ? user : null;
  } else {
    const user = await AsyncStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
};

// Initialize storedUser asynchronously
(async () => {
  storedUser = await initializeStoredUser();
})();

export interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  error: string | null;
  loading: boolean;
  statuscode: string | null;
}

const initialState: AuthState = {
  isAuthenticated: !!storedUser,
  user: storedUser,
  error: null,
  loading: false,
  statuscode: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginRequest(state, action: PayloadAction<LoginPayload>) {
      state.loading = true;
    },
    loginSuccess(state, action: PayloadAction<any>) {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.loading = false;
      state.error = null;
      state.statuscode = action.payload.statusCode;
      if (isWeb) {
        if (secureLS) {
          secureLS.set('user', action.payload);
        }
      } else {
        AsyncStorage.setItem('user', JSON.stringify(action.payload));
      }
    },
    loginFailure(state, action: PayloadAction<any>) {
      state.isAuthenticated = false;
      state.error = action.payload.message;
      state.statuscode = action.payload.statusCode;
      state.loading = false;
      if (isWeb) {
        if (secureLS) {
          secureLS.remove('user');
        } else {
          AsyncStorage.removeItem('user');
        }
      }
    },
    logout(state) {
      state.user = null;
      state.isAuthenticated = false;
      if (isWeb) {
        AsyncStorage.removeItem('user');
        if (secureLS) {
          secureLS.remove('user');
          AsyncStorage.removeItem('tokens');
        }
      }
    },
    resetAuthSlice(state) {
      return initialState; // Reset the slice to its initial state
    },
  },
});

export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  resetAuthSlice,
} = authSlice.actions;
export default authSlice.reducer;
